import { useQuery } from "react-query";
import {
  FairoTraceFetchOptions,
  MLFlowModelVersionsFetchOptions,
  TraceArtifactFetchOptions,
} from "../models/types";
import {
  getExperiment,
  getExperiments,
  getFairoTrace,
  getFairoTraces,
  getMLFlowModels,
  getMLFlowModelVersions,
  getTraceArtifact,
} from "../services/MLFlowService";
import queryClient, { QueryKey } from "../state/QueryStore";

export const useMLFlowModels = (search: string) => {
  return useQuery([QueryKey.MLFlowModel], async () => {
    const { data } = await getMLFlowModels(search);
    if (data.registered_models) return data.registered_models;
    return [];
  });
};

export const fetchMLFlowModels = ({ search }: { search: string }) => {
  return queryClient.fetchQuery([QueryKey.MLFlowModel], async () => {
    const { data } = await getMLFlowModels(search);
    return data;
  });
};

export const searchMLFlowModels = async (searchText: string) => {
  const models = await fetchMLFlowModels({ search: searchText });
  if (models.registered_models) return models.registered_models;
  return [];
};

export const useMLFlowModelVersions = (params: MLFlowModelVersionsFetchOptions) => {
  return useQuery([QueryKey.MlflowVersions, params], async () => {
    const data = await getMLFlowModelVersions(params);
    return data;
  });
};

export const fetchMLFlowModelVersions = (params: MLFlowModelVersionsFetchOptions) => {
  return queryClient.fetchQuery([QueryKey.MLFlowModel], async () => {
    const data = await getMLFlowModelVersions(params);
    return data;
  });
};

export const searchMLFlowModelVersions =
  (params: MLFlowModelVersionsFetchOptions) => async (searchText: string) => {
    const data = await fetchMLFlowModelVersions({ search: searchText, ...params });
    return data.results;
  };

export const useFairoTraces = (params: FairoTraceFetchOptions) => {
  return useQuery([QueryKey.FairoTrace, params], async () => {
    const { data } = await getFairoTraces(params);
    return data;
  });
};

export const useFairoTrace = (requestId: string) => {
  return useQuery([QueryKey.FairoTrace, requestId], async () => {
    const { data } = await getFairoTrace(requestId);
    return data.trace_info;
  });
};

export const useTraceArtifact = (params: TraceArtifactFetchOptions) => {
  return useQuery([QueryKey.TraceArtifact, params], async () => {
    const { data } = await getTraceArtifact(params);
    return data;
  });
};

export const useExperiments = () => {
  return useQuery([QueryKey.Experiment], async () => {
    const { data } = await getExperiments();
    return {
      count: data.experiments.length,
      next: data.next_page_token || null,
      previous: null,
      results: data.experiments,
    };
  });
};

export const useExperiment = (id: string) => {
  return useQuery([QueryKey.Experiment, id], async () => {
    if (!id) return;
    const { data } = await getExperiment(id);
    return data.experiment;
  });
};
